import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { EMOTE_CATALOG } from './cosmetics';
import './PetEmoteAnimations.css';

/**
 * EmotePlayer - wraps children (PetRenderer) and applies emote animations + particle effects.
 *
 * Props:
 *   emoteId    - which emote to play (null = idle)
 *   onComplete - called when a non-looping emote finishes
 *   children   - the PetRenderer element to animate
 */
export default function EmotePlayer({ emoteId, onComplete, children }) {
  const [activeEmote, setActiveEmote] = useState(null);
  const [particles, setParticles] = useState([]);
  const [particleKey, setParticleKey] = useState(0);

  const emote = useMemo(() =>
    emoteId ? EMOTE_CATALOG.find(e => e.id === emoteId) : null,
  [emoteId]);

  // Start emote when emoteId changes
  useEffect(() => {
    if (!emote) {
      setActiveEmote(null);
      setParticles([]);
      return;
    }

    setActiveEmote(emote);
    setParticleKey(k => k + 1);

    // Spawn particles if needed
    if (emote.particles) {
      spawnParticles(emote.particles);
    }

    // Auto-complete non-looping emotes
    if (!emote.loop) {
      const timer = setTimeout(() => {
        setActiveEmote(null);
        setParticles([]);
        onComplete?.();
      }, emote.duration * 1000);
      return () => clearTimeout(timer);
    }
  }, [emoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const spawnParticles = useCallback((type) => {
    const newParticles = [];
    switch (type) {
      case 'hearts':
        for (let i = 0; i < 6; i++) {
          newParticles.push({
            type: 'heart',
            id: i,
            style: {
              left: `${30 + Math.random() * 40}%`,
              top: `${20 + Math.random() * 30}%`,
              animationDelay: `${i * 0.3}s`,
            },
          });
        }
        break;
      case 'stars':
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          newParticles.push({
            type: 'star',
            id: i,
            style: {
              left: '50%',
              top: '40%',
              '--dx': `${Math.cos(angle) * 35}px`,
              '--dy': `${Math.sin(angle) * 35}px`,
              animationDelay: `${i * 0.08}s`,
            },
          });
        }
        break;
      case 'zzz':
        for (let i = 0; i < 3; i++) {
          newParticles.push({
            type: 'zzz',
            id: i,
            text: i === 0 ? 'z' : i === 1 ? 'Z' : 'Z',
            style: {
              right: `${15 + i * 8}%`,
              top: `${15 + i * 5}%`,
              fontSize: `${12 + i * 4}px`,
              animationDelay: `${i * 0.8}s`,
            },
          });
        }
        break;
      case 'impact':
        for (let i = 0; i < 5; i++) {
          const angle = -90 + (i - 2) * 25;
          const rad = (angle * Math.PI) / 180;
          newParticles.push({
            type: 'impact',
            id: i,
            style: {
              left: `${45 + i * 5}%`,
              bottom: '15%',
              transform: `rotate(${angle}deg)`,
              animationDelay: `${i * 0.06}s`,
            },
          });
        }
        break;
      default:
        break;
    }
    setParticles(newParticles);
  }, []);

  const animClass = activeEmote ? `emote-${activeEmote.id}` : '';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        key={`${emoteId}-${particleKey}`}
        className={animClass}
        style={{ transformOrigin: 'center bottom' }}
      >
        {children}
      </div>
      {particles.length > 0 && (
        <div className="emote-particles" key={`p-${particleKey}`}>
          {particles.map((p) => {
            if (p.type === 'heart') {
              return <span key={p.id} className="particle-heart" style={p.style}>&#x2764;</span>;
            }
            if (p.type === 'star') {
              return <span key={p.id} className="particle-star" style={p.style}>&#x2B50;</span>;
            }
            if (p.type === 'zzz') {
              return <span key={p.id} className="particle-zzz" style={p.style}>{p.text}</span>;
            }
            if (p.type === 'impact') {
              return <div key={p.id} className="particle-impact" style={p.style} />;
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}
